import { FormControl, Grid, Paper, Button, Box, CircularProgress, InputLabel, Select, MenuItem, ListItemIcon, ListItemText, Tabs, Tab, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../reducers';
import { useAppDispatch } from '../../../../store/store';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { moneyFormatter } from '../../../../utils/moneyFormat';
import Authorization from '../../../../utils/Authorization';
import b64toBlob from 'b64-to-blob';
import { fetchHighCostMembers, exportHighCostMembers, MenuProps } from '../../../Widgets/WidgetService';
import { getLastClaimUpdatedOn } from '../../Analytics/AnalyticService';
import TeamDropdown from '../../../Shared/TeamDropdown';
import { fetchTeams } from '../../../../store/careTeams.slice';
import { useHealthPlanContext } from '../../../../hooks/healthPlanContext';
import TopTenMembers from '../../../../Models/Claims/TopTenMembers.model';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { formatDate } from '../../../../utils/dateHelpers';

const monthsList = [
    "January", "February", "March", "April",
    "May", "June", "July", "August",
    "September", "October", "November", "December"
];
const HighestCostMembers = (props) => {
    const currentYear = new Date().getFullYear();
    const monthOptions = Array.from({ length: 12 }, (_, i) => (i + 1).toString());
    const yearOptions = [...Array.from({ length: 4 }, (_, i) => currentYear - i), 0];
    const recentYears = [
        (currentYear - 1).toString(), // previous year
        "0" // baseline
    ];

    const getYearLabel = (y: number) => (y === 0 ? "Baseline" : y.toString());

    const dispatch = useAppDispatch();
    const authData = new Authorization();
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [highestCostMembers, setHighestCostMembers] = useState<TopTenMembers[]>([]);
    const [selectedTeam, setSelectedTeam] = useState(authData.getDefaultTeamId(props.hasElevatedFilterAccess ? 'All' : ''));
    const [isLoading, setIsLoading] = useState(false);
    const [showInActiveTeams, setShowInActiveTeams] = useState(false);
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const [paginationModel, setPaginationModel] = useState({ pageSize: 10, page: 0 });
    const [healthPlanIds, setHealthPlanIds] = useState<string[]>([]);
    const [years, setYears] = useState<string[]>(recentYears.map(String));
    const [months, setMonths] = useState<string[]>(monthOptions.map(String));
    const { population } = useSelector((state: RootState) => state.adminMembers);
    const [lastUpdatedOn, setLastUpdatedOn] = useState('');
    const { healthPlans } = useHealthPlanContext();
    const filteredHealthPlans = healthPlans
        .filter((item) => {
            return population.map((item) => item.HealthPlan?.Name).includes(item.Name);
        })
        .sort((a, b) => a.Name.localeCompare(b.Name));

    const safeMonths = months?.map(Number) || [];

    const [selectedYear, setSelectedYear] = useState(0);

    useEffect(() => {
        if (Boolean(teams.length === 0)) {
            dispatch(fetchTeams(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        const data = await getLastClaimUpdatedOn();
        setLastUpdatedOn(data ? formatDate(new Date(`${data.split('T')[0]}T00:00:00`), 'MM/dd/yyyy') : '');
    }, [isFetched]);

    useEffect(() => {
        initialPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps;
    }, []);

    useEffect(() => {
        if (filteredHealthPlans.length >= 2 && setHealthPlanIds && healthPlanIds.length === 0) {
            setHealthPlanIds([
                filteredHealthPlans[0].Id,
                filteredHealthPlans[1].Id
            ]);
        }
    }, [filteredHealthPlans]);

    const columns: GridColDef[] = [
        {
            field: 'Rank',
            headerName: '#',
            maxWidth: 70,
        },
        {
            field: 'MemberName',
            headerName: 'Member Name',
            flex: 1,
        },
        {
            field: 'Cost',
            headerName: 'Cost',
            flex: 1,
            valueGetter: (value, row) => `${moneyFormatter.format(row.Cost)}`,
        },
    ];

    useEffect(() => {
        if (!isFetched && healthPlanIds && healthPlanIds.length > 0) {
            changeFilter();
            setIsLoading(true);
            setIsFetched(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps;
    }, [healthPlanIds]);

    const changeFilter = async () => {
        try {
            setIsLoading(true);
            const response = await fetchHighCostMembers(healthPlanIds.toString(), selectedTeam, years.toString(), months.toString(), showInActiveTeams);
            setHighestCostMembers(response);
            setSelectedYear(0);
            setIsLoading(false);
        } catch (error) {
            setHighestCostMembers([]);
            setIsLoading(false);
            console.log(error);
        }
    };

    const handleDownload = async () => {
        try {
            setDownLoading(true);
            await exportHighCostMember();
        } finally {
            setDownLoading(false);
        }
    };

    const exportHighCostMember = async () => {
        const response = await exportHighCostMembers(healthPlanIds.toString(), selectedTeam, years.toString(), months.toString(), showInActiveTeams);
        let blob = b64toBlob(response.base64String, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `HighCostMembers - ${dateInfo}.xlsx`);
        link.click();
        link.remove();
    };

    return (
        <Grid size={{ xs: 12, lg: 12 }}>
            <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 480, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingTop={1}>
                    {props.hasElevatedFilterAccess && (
                        <Grid size={2.6}>
                            <TeamDropdown
                                teams={teams}
                                selectedTeam={selectedTeam}
                                onChange={(id) => setSelectedTeam(id)}
                                showInactive={showInActiveTeams}
                                onToggleInactive={setShowInActiveTeams}
                            />
                        </Grid>
                    )}
                    <Grid size={2.6}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Health Plan</InputLabel>
                            <Select
                                multiple
                                value={Array.isArray(healthPlanIds) ? healthPlanIds : []}
                                label="Health Plan"
                                name="plan"
                                onChange={(e) => {
                                    let value = e.target.value;
                                    value = typeof value === 'string' ? value.split(',') : value;

                                    if (value.includes("All")) {
                                        const allIds = filteredHealthPlans.map(p => p.Id);
                                        setHealthPlanIds(allIds);
                                    } else {
                                        setHealthPlanIds(value);
                                    }
                                }}
                                renderValue={(selected) => {
                                    if (selected.length === filteredHealthPlans.length) {
                                        return "All";
                                    }

                                    return filteredHealthPlans
                                        .filter(plan => selected.includes(plan.Id))
                                        .map(plan => plan.Name)
                                        .join(", ");
                                }}
                                MenuProps={MenuProps}
                            >
                                <MenuItem value="All">
                                    <ListItemIcon>
                                        {healthPlanIds.length === filteredHealthPlans.length && (
                                            <span style={{ fontSize: "14px" }}>✔</span>
                                        )}
                                    </ListItemIcon>
                                    <ListItemText primary="All" />
                                </MenuItem>

                                {filteredHealthPlans.map((plan) => (
                                    <MenuItem key={plan.Id} value={plan.Id}>
                                        <ListItemIcon>
                                            {healthPlanIds.includes(plan.Id) && (
                                                <span style={{ fontSize: "14px" }}>✔</span>
                                            )}
                                        </ListItemIcon>
                                        <ListItemText primary={plan.Name} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={2.6}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Year</InputLabel>
                            <Select
                                multiple
                                value={years}
                                label="Year"
                                onChange={(e) => {
                                    let value = e.target.value;
                                    value = typeof value === "string" ? value.split(",") : value;

                                    if (value.includes("All")) {
                                        setYears(yearOptions.map(String));
                                    } else if (value.length === 0) {
                                        setYears([String(currentYear)]);
                                    } else {
                                        setYears(value);
                                    }
                                }}
                                renderValue={(selected) => {
                                    if (!selected || selected.length === 0) return "";
                                    if (selected.length === yearOptions.length) return "All";
                                    return selected.map((y) => getYearLabel(Number(y))).join(", ");
                                }}
                            >
                                <MenuItem value="All">
                                    <ListItemIcon>
                                        {years.length === yearOptions.length && (
                                            <span style={{ fontSize: "14px" }}>✔</span>
                                        )}
                                    </ListItemIcon>
                                    <ListItemText primary="All" />
                                </MenuItem>

                                {yearOptions.map((y) => (
                                    <MenuItem key={y} value={String(y)}>
                                        <ListItemIcon>
                                            {years.includes(String(y)) && (
                                                <span style={{ fontSize: "14px" }}>✔</span>
                                            )}
                                        </ListItemIcon>
                                        <ListItemText primary={getYearLabel(y)} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={2.6}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Month</InputLabel>
                            <Select
                                multiple
                                value={safeMonths}
                                label="Month"
                                onChange={(e) => {
                                    let value = e.target.value;

                                    value = typeof value === "string"
                                        ? value.split(",").map(Number)
                                        : value;

                                    if (value.length === 0) {
                                        setMonths(monthOptions.map(String)); // fallback ALL
                                    } else {
                                        setMonths(value.map(String)); // ✅ convert back to string
                                    }
                                }}
                                renderValue={(selected) => {
                                    if (!selected || selected.length === 0) return "All";

                                    if (selected.length === monthOptions.length) return "All";

                                    return selected.map((m: number) => monthsList[m - 1]).join(", ");
                                }}
                            >
                                <MenuItem onClick={() => setMonths(monthOptions.map(String))}>
                                    <ListItemIcon>
                                        {months.length === monthOptions.length && (
                                            <span style={{ fontSize: "14px" }}>✔</span>
                                        )}
                                    </ListItemIcon>
                                    <ListItemText primary="All" />
                                </MenuItem>

                                {monthsList.map((month, index) => {
                                    const value = index + 1;

                                    return (
                                        <MenuItem key={value} value={value}>
                                            <ListItemIcon>
                                                {safeMonths.includes(value) && (
                                                    <span style={{ fontSize: "14px" }}>✔</span>
                                                )}
                                            </ListItemIcon>
                                            <ListItemText primary={month} />
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={1.5}>
                        <Button
                            style={{ minWidth: '80px' }}
                            variant="contained"
                            onClick={changeFilter}>
                            Submit
                        </Button>
                    </Grid>
                    <Grid size={12} position="relative" sx={{ height: '55vh', width: '100%' }}>
                        {isLoading ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : null}
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={1.5}
                            sx={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                zIndex: 1,
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Last claim updated: {lastUpdatedOn || 'N/A'}
                            </Typography>

                            <Box
                                onClick={!downLoading ? handleDownload : undefined}
                                sx={{
                                    width: 24,
                                    height: 24,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    cursor: downLoading ? 'default' : 'pointer',
                                }}
                            >
                                {downLoading ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <FileDownloadIcon
                                        sx={{
                                            color: 'black',
                                            fontSize: 24,
                                        }}
                                        titleAccess="Export"
                                    />
                                )}
                            </Box>
                        </Box>
                        <Tabs
                            value={selectedYear}
                            onChange={(e, newValue) => setSelectedYear(newValue)}
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            {highestCostMembers?.map((detail, index) => (
                                <Tab key={index} label={detail.Year === '0' ? "Baseline" : detail.Year} />
                            ))}
                        </Tabs>
                        {highestCostMembers?.map((detail, index) => {
                            const rowsWithRank = detail.Members.map((m, i) => ({
                                ...m,
                                Rank: i + 1,
                            }));
                            return (
                                selectedYear === index && (
                                    <Grid container key={`detail-${index}`}>
                                        <Grid size={12} sx={{ height: '55vh', width: '100%' }}>
                                            <div style={{ width: '100%', height: '42vh', overflow: 'auto' }}>
                                                <DataGrid
                                                    rows={rowsWithRank}
                                                    columns={columns}
                                                    paginationModel={paginationModel}
                                                    onPaginationModelChange={setPaginationModel}
                                                    pageSizeOptions={[5]}
                                                    getRowId={(row) => row.Id + row.AhcccsId}
                                                    getRowHeight={() => 45}
                                                    autoHeight={false}
                                                    sx={{
                                                        height: '100%',
                                                        [`& .MuiDataGrid-cell`]: { py: 1 },
                                                    }}
                                                />
                                            </div>
                                        </Grid>
                                    </Grid>
                                )
                            )
                        })}
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    );
};

export default HighestCostMembers;
